import {
  Check as CheckIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, ButtonBase, CircularProgress, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useGroups } from '../contexts/GroupContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useShareIcons } from '../hooks/useShareIcons';
import { useThemeColors } from '../hooks/useThemeColors';
import { getWorkspacesByGroups } from '../services/workspaceService';
import { IMG } from '../theme/assets';
import type { Workspace } from '../types';
import AppDialog from './AppDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  onShare: (workspaceId: string) => Promise<void>;
  title?: string;
  /**
   * 案件を選んだあとに続けて出す枠。マイアイテムからの共有では
   * 「この案件に見せる結果」の切り替えをここに出す。
   */
  renderExtra?: (workspaceId: string) => React.ReactNode;
  /** 共有ボタンを押せるかの追加条件。renderExtra の入力が未完のときに false にする */
  canShare?: boolean;
}

const WorkspaceShareDialog: React.FC<Props> = ({
  open,
  onClose,
  onShare,
  title = '検証結果を案件に共有',
  renderExtra,
  canShare = true,
}) => {
  const { themeMode } = useThemeMode();
  const { groups } = useGroups();
  const tokens = useThemeColors();
  const icons = useShareIcons();
  const [allWorkspaces, setAllWorkspaces] = useState<Workspace[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Theme colors
  const accentColor = themeMode === 'dark' ? '#64ffda' : '#00897b';
  const accentColorLight =
    themeMode === 'dark' ? 'rgba(100, 255, 218, 0.2)' : 'rgba(0, 137, 123, 0.15)';
  const accentColorBorder =
    themeMode === 'dark' ? 'rgba(100, 255, 218, 0.5)' : 'rgba(0, 137, 123, 0.5)';

  // グループに基づいて案件を取得
  useEffect(() => {
    const loadAllWorkspaces = async () => {
      if (groups.length === 0) {
        setAllWorkspaces([]);
        return;
      }
      setLoadingWorkspaces(true);
      try {
        const groupIds = groups.map((g) => g.id);
        const workspaces = await getWorkspacesByGroups(groupIds);
        setAllWorkspaces(workspaces);
      } catch (err) {
        console.error('Error loading workspaces:', err);
        setAllWorkspaces([]);
      } finally {
        setLoadingWorkspaces(false);
      }
    };
    if (open) {
      loadAllWorkspaces();
    }
  }, [groups, open]);

  // 選択されたグループに属する案件をフィルタ
  const filteredWorkspaces = selectedGroupId
    ? allWorkspaces.filter((ws) => ws.groupIds.includes(selectedGroupId))
    : [];

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
    setSelectedWorkspaceId(null);
  };

  const handleBackToGroups = () => {
    setSelectedGroupId(null);
    setSelectedWorkspaceId(null);
  };

  const handleShare = async () => {
    if (!selectedWorkspaceId) return;

    setSharing(true);
    setError(null);

    try {
      await onShare(selectedWorkspaceId);
      handleClose();
    } catch (err) {
      console.error('Share failed:', err);
      setError('共有に失敗しました');
    } finally {
      setSharing(false);
    }
  };

  const handleClose = () => {
    if (!sharing) {
      setSelectedGroupId(null);
      setSelectedWorkspaceId(null);
      setError(null);
      onClose();
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      icon={<Box component="img" src={IMG.workspace.share} alt="" />}
      title={selectedGroupId ? '案件を選択' : title || '案件に共有'}
      subtitle={
        selectedGroupId ? '共有先の案件を選んでください' : 'まず、どのグループに共有するか選びます'
      }
      size="md"
      actions={
        <>
          <Button onClick={handleClose} disabled={sharing}>
            キャンセル
          </Button>
          {selectedGroupId && (
            <Button
              onClick={handleShare}
              variant="contained"
              disabled={sharing || !selectedWorkspaceId || !canShare}
              startIcon={
                sharing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ShareIcon />
              }
              sx={{
                background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                color: '#fff',
                fontWeight: 800,
                borderRadius: '11px',
                textTransform: 'none',
                px: 2,
                boxShadow: '0 8px 20px rgba(40,50,90,0.2)',
                '&:hover': {
                  filter: 'brightness(0.96)',
                  background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                },
                '&:disabled': {
                  background: tokens.line,
                  color: tokens.faint,
                  boxShadow: 'none',
                },
              }}
            >
              {sharing ? '共有中...' : '共有する'}
            </Button>
          )}
        </>
      }
    >
      {/* 1つ前（グループ選び）に戻る。下のボタン列に置くと「やめる」と並んで
          どちらが取り消しか分からなくなるので、中身の上に出す */}
      {selectedGroupId && (
        <Button
          onClick={handleBackToGroups}
          disabled={sharing}
          startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
          sx={{
            mb: 1.5,
            ml: -0.75,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 12.5,
            color: tokens.muted,
            '&:hover': { color: tokens.teal, background: 'transparent' },
          }}
        >
          グループを選び直す
        </Button>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loadingWorkspaces ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={32} />
        </Box>
      ) : !selectedGroupId ? (
        groups.length === 0 ? (
          <Alert severity="info">グループがありません。まずグループを作成してください。</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {groups.map((group) => {
              const groupWorkspaces = allWorkspaces.filter((ws) => ws.groupIds.includes(group.id));
              const color = group.color || accentColor;
              return (
                <ButtonBase
                  key={group.id}
                  onClick={() => handleGroupSelect(group.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.75,
                    width: '100%',
                    px: 1.75,
                    py: 1.5,
                    borderRadius: '12px',
                    border: `1px solid ${tokens.line}`,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    transition: 'border-color .15s, background-color .15s',
                    '&:hover': { bgcolor: accentColorLight, borderColor: accentColorBorder },
                  }}
                >
                  {/* グループの色の丸＋頭文字。一覧の出し方と揃える */}
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                      borderRadius: '11px',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: color,
                      color: '#fff',
                      fontWeight: 900,
                      fontSize: 17,
                    }}
                  >
                    {group.name.slice(0, 1)}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: 14,
                        color: tokens.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {group.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: tokens.muted }}>
                      {groupWorkspaces.length === 0
                        ? '案件がまだありません'
                        : `案件 ${groupWorkspaces.length} 件`}
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: tokens.faint, fontSize: 20 }} />
                </ButtonBase>
              );
            })}
          </Box>
        )
      ) : filteredWorkspaces.length === 0 ? (
        <Alert severity="info">このグループには案件がありません。</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredWorkspaces.map((ws) => {
            const picked = selectedWorkspaceId === ws.id;
            return (
              <ButtonBase
                key={ws.id}
                onClick={() => setSelectedWorkspaceId(ws.id)}
                disabled={sharing}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  width: '100%',
                  px: 1.75,
                  py: 1.5,
                  borderRadius: '12px',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  border: `1px solid ${picked ? accentColorBorder : tokens.line}`,
                  bgcolor: picked ? accentColorLight : 'transparent',
                  transition: 'border-color .15s, background-color .15s',
                  '&:hover': { bgcolor: accentColorLight },
                }}
              >
                <Box
                  component="img"
                  src={icons.stepProject}
                  alt=""
                  sx={{ width: 38, height: 38, objectFit: 'contain', flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 14,
                      color: tokens.ink,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {ws.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: tokens.muted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {ws.description || 'この案件に結果を見せます'}
                  </Typography>
                </Box>
                {/* 選んだものだけチェックを出す */}
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    border: `2px solid ${picked ? accentColor : tokens.line}`,
                    bgcolor: picked ? accentColor : 'transparent',
                  }}
                >
                  {picked && <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
                </Box>
              </ButtonBase>
            );
          })}
          {/* 案件を選んだあとの追加の指定（呼び出し側が出す） */}
          {selectedWorkspaceId && renderExtra?.(selectedWorkspaceId)}
        </Box>
      )}
    </AppDialog>
  );
};

export default WorkspaceShareDialog;
